import { MouseEventHandler, ReactNode, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

import MoveVerticalIcon from '@paradigm/design-system/src/assets/MoveVerticalIcon';
import { useBottomTabsToggleState } from '#/contexts/BottomTabsStateProvider';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import localStorage from '@paradigm/utils/src/safeLocalStorage';

interface Props {
  /**
   * Must be a globally unique value per split screen in the app.
   * This value is used to persist the configuration of the split
   * screen in the local storage, so the configuration persists into
   * the next session.
   */
  readonly id: string;
  readonly top: ReactNode;
  readonly bottom: ReactNode;
}

export default function SplitScreen(props: Props) {
  const { id, top, bottom } = props;
  const bottomViewRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isVisible: showBottom } = useBottomTabsToggleState();

  useLayoutEffect(() => {
    if (!showBottom) return;
    const flex = getLastBottomFlex(id);
    bottomViewRef.current?.style.setProperty('--flex', flex.toString());
  }, [id, showBottom]);

  const handleResize: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    const bottomEl = bottomViewRef.current;
    const containerEl = containerRef.current;
    if (bottomEl == null || containerEl == null) return;

    const totalHeight = containerEl.clientHeight;
    const maxHeight = totalHeight - 75;
    const initialHeight = bottomEl.offsetHeight;
    const initialY = event.pageY;

    const handlePointerMove = (pointerEvent: PointerEvent) => {
      pointerEvent.preventDefault();
      const offsetY = initialY - pointerEvent.pageY;
      const unboundedHeight = initialHeight + offsetY;
      const height = Math.max(40, Math.min(maxHeight, unboundedHeight));
      const flex = height / (totalHeight - height);
      bottomEl.style.setProperty('--flex', flex.toString());
    };

    const handleMouseUp = () => {
      persistBottomFlex(id, getCssCustomPropFlex(bottomEl));
      document.removeEventListener('pointermove', handlePointerMove);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('mouseup', handleMouseUp, { once: true });
  };

  return (
    <Container ref={containerRef}>
      {top}
      {showBottom && (
        <BottomView ref={bottomViewRef}>
          <Handle onMouseDown={handleResize} />
          {bottom}
        </BottomView>
      )}
    </Container>
  );
}

const DEFAULT_BOTTOM_FLEX = 0.65;

function getLastBottomFlex(id: string) {
  const flexStr = localStorage.getItem(getStorageKey(id));
  if (flexStr != null) {
    const parsedFlex = Number.parseFloat(flexStr);
    if (Number.isFinite(parsedFlex)) return parsedFlex;
  }
  return DEFAULT_BOTTOM_FLEX;
}

function persistBottomFlex(id: string, flex: number) {
  localStorage.setItem(getStorageKey(id), flex.toString());
}

function getStorageKey(id: string) {
  return `split-screen-flex/${id}`;
}

function getCssCustomPropFlex(element: HTMLElement) {
  return Number.parseFloat(
    getComputedStyle(element).getPropertyValue('--flex'),
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  /* Defensive: lets the flex column shrink below its content size so a
     long-content child (e.g. the Block Terminal chat) can scroll
     internally instead of pushing the panel past the viewport. */
  min-height: 0;
`;

const BottomView = styled.div`
  position: relative;
  flex: var(--flex);
  /* min-height: 0 lets flex children with overflow-y: auto actually
     scroll instead of growing to fit their content (which would push
     this view past its --flex ratio and overflow the page). */
  min-height: 0;
  box-shadow: 0 -4px 20px 0 rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    flex: 1;
  }
`;

const Handle = styled.button.attrs({
  tabIndex: -1,
  children: <MoveVerticalIcon style={{ display: 'block' }} />,
  title: 'Resize vertically',
})`
  position: absolute;
  top: -14px;
  right: 16px;
  cursor: ns-resize !important;
  color: #5689c3;
  background: #041d39;
  border: 0;
  padding: 5px 10px;
  height: 14px;
  width: 32px;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
`;
